import React, { useState } from 'react';

import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { InputLabel } from '@mui/material';
import { AlineInput } from 'src/AlineInput';
import { AlineTooltip } from 'src/components';
import type { NotificationOffset } from 'src/components/ai/reminder-automation/NotificationOffsetBuilder';
import { GenerateInsightIcon } from 'src/components/icons/GenerateInsightIcon';
import { buildAiReasoningTooltipContent } from 'src/components/reminders/AiReasoningTooltip';
import type { Apollo } from 'src/gql/generated';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import { ReminderAssignees } from './ReminderAssignees';
import { ReminderDescription } from './ReminderDescription';
import type { ReminderDocumentType } from './ReminderDocument';
import { ReminderDocument } from './ReminderDocument';
import { ReminderDueDate } from './ReminderDueDate';
import { ReminderNotifyOn } from './ReminderNotifyOn';
import { ReminderTypePicker } from './ReminderType';
import type { ColumnOptionSubset } from '../useReportColumnMap';
import { buildColumnMap } from '../useReportColumnMap';

const UPDATE_REMINDER_TITLE = gql`
  mutation UpdateReminderTitle($input: UpdateReminderInput!) {
    updateReminder(input: $input) {
      id
      title
    }
  }
`;

const UPDATE_REMINDER_DESCRIPTION = gql`
  mutation UpdateReminderDescription($input: UpdateReminderInput!) {
    updateReminder(input: $input) {
      id
      description
    }
  }
`;

const UPDATE_REMINDER_DUE_DATE = gql`
  mutation UpdateReminderDueDate($input: UpdateReminderInput!) {
    updateReminder(input: $input) {
      id
      dueDate
    }
  }
`;

const UPDATE_REMINDER_TYPE = gql`
  mutation UpdateReminderType($input: UpdateReminderInput!) {
    updateReminder(input: $input) {
      id
      type
    }
  }
`;

const UPDATE_REMINDER_NOTIFICATION_OFFSETS = gql`
  mutation UpdateReminderNotificationOffsets($input: UpdateReminderInput!) {
    updateReminder(input: $input) {
      id
      notificationOffsets {
        amount
        unit
        position
        notifiedAt
      }
    }
  }
`;

export const useReminderMainStyles = makeStyles()(theme => ({
  aiReasoningIcon: {
    cursor: 'pointer',
    display: 'flex',
    marginLeft: theme.spacing(1),
  },
  aiReasoningRow: {
    alignItems: 'center',
    display: 'flex',
  },
  fieldLabel: {
    color: theme.appColors.iconsType,
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  inputArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  main: {
    overflowX: 'auto',
    padding: theme.spacing(1, 2, 2),
  },
  row: {
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(2),
    minHeight: 52,
    padding: theme.spacing(1, 0),
  },
  section: {
    marginTop: theme.spacing(2),
  },
  titleInputBorder: {
    borderRadius: 4,
  },
  titleInputText: {
    fontSize: 20,
    fontWeight: 600,
    padding: theme.spacing(1),
  },
}));

export interface ReminderDetailsReminder extends Pick<
  Apollo.Reminder,
  | 'aiRationale'
  | 'dueDate'
  | 'description'
  | 'id'
  | 'isAiGenerated'
  | 'title'
  | 'type'
  | '__typename'
> {
  aiReminderAutomation:
    | (Pick<Apollo.AiReminderAutomation, 'id' | 'title'> & {
        report: Pick<Apollo.Report, 'id' | 'name'> & { columnOptions: ColumnOptionSubset[] };
      })
    | null;
  assignees: UserSubset[];
  document: ReminderDocumentType;
  notificationOffsets: NotificationOffset[];
}

export function ReminderDetailsMain({ reminder }: { reminder: ReminderDetailsReminder }) {
  const reminderId = reminder.id;
  const client = useApolloClient();
  const { classes } = useReminderMainStyles();
  const [title, setTitle] = useState(reminder.title);
  const [description, setDescription] = useState(reminder.description);
  const [offsets, setOffsets] = useState(reminder.notificationOffsets);
  const debouncedUpdateTitle = useDebouncedCallback(async (newTitle: string) => {
    await client.mutate({
      mutation: UPDATE_REMINDER_TITLE,
      optimisticResponse: {
        updateReminder: { __typename: 'Reminder', id: reminderId, title: newTitle },
      },
      variables: { input: { props: { title: newTitle }, reminderId } },
    });
  }, 500);

  const debouncedUpdateDescription = useDebouncedCallback(async (newDescription: string) => {
    await client.mutate({
      mutation: UPDATE_REMINDER_DESCRIPTION,
      optimisticResponse: {
        updateReminder: { __typename: 'Reminder', description: newDescription, id: reminderId },
      },
      variables: { input: { props: { description: newDescription }, reminderId } },
    });
  }, 500);

  const debouncedUpdateOffsets = useDebouncedCallback(async (newOffsets: NotificationOffset[]) => {
    await client.mutate({
      mutation: UPDATE_REMINDER_NOTIFICATION_OFFSETS,
      optimisticResponse: {
        updateReminder: {
          __typename: 'Reminder',
          id: reminderId,
          notificationOffsets: newOffsets.map(o => ({
            __typename: 'NotificationOffset',
            amount: o.amount,
            notifiedAt: null,
            position: o.position,
            unit: o.unit,
          })),
        },
      },
      variables: {
        input: {
          props: {
            notificationOffsets: newOffsets.map(({ amount, position, unit }) => ({
              amount,
              position,
              unit,
            })),
          },
          reminderId,
        },
      },
    });
  }, 500);

  return (
    <div className={classes.main}>
      <AlineInput
        classes={{ input: classes.titleInputText }}
        inputClassName={classes.titleInputBorder}
        placeholder="Type a title..."
        value={title}
        onChange={e => {
          setTitle(e.target.value);
          debouncedUpdateTitle(e.target.value);
        }}
      />

      <div className={classes.section}>
        <InputLabel className={classes.fieldLabel}>Description</InputLabel>
        <ReminderDescription
          description={description}
          onDescriptionChange={newDescription => {
            setDescription(newDescription);
            debouncedUpdateDescription(newDescription);
          }}
        />
      </div>

      <div className={classes.section}>
        <InputLabel className={classes.fieldLabel}>Assignees</InputLabel>
        <ReminderAssignees reminder={reminder} />
      </div>

      <div className={classes.section}>
        <InputLabel className={classes.fieldLabel}>Due Date</InputLabel>
        <div className={classes.aiReasoningRow}>
          <div style={{ width: 'fit-content' }}>
            <ReminderDueDate
              dueDate={reminder.dueDate}
              onDueDateChange={async dueDate => {
                await client.mutate({
                  mutation: UPDATE_REMINDER_DUE_DATE,
                  optimisticResponse: {
                    updateReminder: { __typename: 'Reminder', dueDate, id: reminderId },
                  },
                  variables: { input: { props: { dueDate }, reminderId } },
                });
              }}
            />
          </div>
          {reminder.isAiGenerated && (
            <AlineTooltip
              arrow
              disableInteractive={false}
              placement="top"
              slotProps={{ tooltip: { sx: { maxWidth: 420, px: 2, py: 1 } } }}
              title={buildAiReasoningTooltipContent({
                ...reminder,
                columnMap: buildColumnMap(
                  reminder.aiReminderAutomation?.report?.columnOptions ?? []
                ),
                documentId: reminder.document.id,
              })}
            >
              <span className={classes.aiReasoningIcon}>
                <GenerateInsightIcon height={20} width={20} />
              </span>
            </AlineTooltip>
          )}
        </div>
      </div>

      <div className={classes.section}>
        <InputLabel className={classes.fieldLabel}>Notification</InputLabel>
        <ReminderNotifyOn
          offsets={offsets}
          onOffsetsChange={newOffsets => {
            setOffsets(newOffsets);
            debouncedUpdateOffsets(newOffsets);
          }}
        />
      </div>

      <div className={classes.section}>
        <InputLabel className={classes.fieldLabel}>Type</InputLabel>
        <ReminderTypePicker
          type={reminder.type}
          onTypeChange={async type => {
            await client.mutate({
              mutation: UPDATE_REMINDER_TYPE,
              optimisticResponse: {
                updateReminder: { __typename: 'Reminder', id: reminderId, type },
              },
              variables: { input: { props: { type }, reminderId } },
            });
          }}
        />
      </div>

      <div className={classes.section}>
        <InputLabel className={classes.fieldLabel}>Document</InputLabel>
        <ReminderDocument reminder={{ document: reminder.document }} />
      </div>
    </div>
  );
}
